/**
 * InlineProposal Component
 *
 * Displays a proposal card inline within a chat message bubble.
 * Supports collapsed (summary) and expanded (detail) views,
 * with approve/reject actions directly in the chat flow.
 */

import {
  Component,
  Show,
  For,
  createSignal,
  createMemo,
  createEffect,
} from "solid-js";
import { Button } from "../ui/Button";
import { Dropdown, DropdownItem } from "../ui/Dropdown";
import { Spinner } from "../ui/Spinner";
import { useToast } from "../../hooks";
import type {
  Proposal,
  ProposalId,
  ProposalStatus,
  ProposedActionId,
  ActionStatus,
} from "../../api/types";
import type { ProposalOperationResult } from "../../hooks/useProposals";
import { formatActionSummary } from "./formatActionSummary";
import { groupActions, isQueryTool } from "../proposals/groupActions";
import { ProposalImpactSummary } from "../proposals/ProposalImpactSummary";
import { ActionGroupSection } from "../proposals/ActionGroupSection";

export interface InlineProposalProps {
  /** The hydrated proposal (loaded with the conversation). Undefined while the
   *  conversation is still loading or if the proposal could not be hydrated. */
  proposal: Proposal | undefined;
  onApprove: (
    id: ProposalId,
    actionIds?: ProposedActionId[],
  ) => Promise<ProposalOperationResult>;
  onReject: (id: ProposalId) => Promise<ProposalOperationResult>;
  onProposalActioned?: () => void;
}

const topRuleByStatus: Record<ProposalStatus, string> = {
  pending: "border-t-signal-magenta",
  approved: "border-t-signal-blue",
  executing: "border-t-signal-blue",
  executed: "border-t-signal-green",
  partially_approved: "border-t-signal-amber",
  partially_executed: "border-t-signal-amber",
  rejected: "border-t-signal-red",
  expired: "border-t-line-strong",
};

export const InlineProposal: Component<InlineProposalProps> = (props) => {
  const { showError, showSuccess } = useToast();

  // The proposal is hydrated with the conversation and lives in the conversation
  // store; approve/reject upsert it there, so this prop updates reactively.
  const proposal = () => props.proposal;

  const [expanded, setExpanded] = createSignal(true);
  const [selectedActions, setSelectedActions] = createSignal<
    Set<ProposedActionId>
  >(new Set());
  const [isExecuting, setIsExecuting] = createSignal(false);

  const pendingActions = createMemo(() => {
    const p = proposal();
    if (!p) return [];
    return p.actions.filter(
      (a) => a.status === "pending" && !isQueryTool(a.tool_name),
    );
  });

  const groups = createMemo(() => {
    const p = proposal();
    if (!p) return [];
    return groupActions(p.actions).filter((g) => !g.isReadOnly);
  });

  createEffect(() => {
    const pending = pendingActions();
    if (pending.length > 0) {
      setSelectedActions(new Set(pending.map((a) => a.id)));
    }
  });

  const selectedCount = createMemo(() => selectedActions().size);

  const canAct = createMemo(() => {
    const p = proposal();
    if (!p) return false;
    return p.status === "pending" || p.status === "partially_approved";
  });

  const isResolved = createMemo(() => {
    const p = proposal();
    if (!p) return false;
    return (
      p.status === "executed" ||
      p.status === "partially_executed" ||
      p.status === "rejected" ||
      p.status === "expired"
    );
  });

  const isRunning = createMemo(() => {
    const p = proposal();
    return p?.status === "executing" || p?.status === "approved";
  });

  const toggleAction = (actionId: ProposedActionId) => {
    setSelectedActions((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) {
        next.delete(actionId);
      } else {
        next.add(actionId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedActions(new Set(pendingActions().map((a) => a.id)));
  };

  const deselectAll = () => {
    setSelectedActions(new Set<ProposedActionId>());
  };

  const toggleGroup = (groupId: string) => {
    const group = groups().find((g) => g.id === groupId);
    if (!group) return;
    const pending = group.actions.filter((a) => a.status === "pending");
    const allSelected = pending.every((a) => selectedActions().has(a.id));
    setSelectedActions((prev) => {
      const next = new Set(prev);
      if (allSelected) pending.forEach((a) => next.delete(a.id));
      else pending.forEach((a) => next.add(a.id));
      return next;
    });
  };

  const handleApprove = async () => {
    const p = proposal();
    if (!p) return;
    setIsExecuting(true);
    try {
      const selected = Array.from(selectedActions());
      const allSelected = selected.length === pendingActions().length;
      await props.onApprove(p.id, allSelected ? undefined : selected);
      showSuccess("Proposal approved");
      props.onProposalActioned?.();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to approve proposal",
      );
    } finally {
      setIsExecuting(false);
    }
  };

  const handleReject = async () => {
    const p = proposal();
    if (!p) return;
    setIsExecuting(true);
    try {
      await props.onReject(p.id);
      showSuccess("Proposal rejected");
      props.onProposalActioned?.();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to reject proposal",
      );
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div class="mt-3 pt-3 border-t border-line">
      <Show
        when={proposal()}
        fallback={
          <div class="flex items-center gap-2 p-3 bg-surface rounded border border-line border-t-2 border-t-line-strong">
            <Spinner size="sm" />
            <span class="text-xs text-ink-3">Loading proposal...</span>
          </div>
        }
      >
        {(p) => (
          <div
            class={
              "bg-surface border border-line border-t-2 rounded p-3 " +
              topRuleByStatus[p().status]
            }
          >
            {/* Header */}
            <button
              type="button"
              class="w-full flex items-center justify-between gap-2 text-left"
              onClick={() => canAct() && setExpanded(!expanded())}
            >
              <div class="flex items-center gap-1.5 min-w-0 flex-wrap">
                <StatusIcon status={p().status} />
                <span class="text-xs font-medium text-ink-2">Proposal</span>
                <span class="text-xs text-ink-3">&middot;</span>
                <Show
                  when={isResolved()}
                  fallback={
                    <span class="text-xs text-ink-3">
                      {
                        p().actions.filter((a) => !isQueryTool(a.tool_name))
                          .length
                      }{" "}
                      action
                      {p().actions.filter((a) => !isQueryTool(a.tool_name))
                        .length !== 1
                        ? "s"
                        : ""}
                    </span>
                  }
                >
                  <span class="text-xs text-ink-3">
                    <StatusLabel status={p().status} />
                  </span>
                </Show>
              </div>
              <Show when={canAct()}>
                <ChevronIcon expanded={expanded()} />
              </Show>
            </button>

            {/* Expired state */}
            <Show when={p().status === "expired"}>
              <p class="mt-2 text-xs text-ink-3">
                This proposal is no longer available.
              </p>
            </Show>

            {/* Rejected state */}
            <Show when={p().status === "rejected"}>
              <p class="mt-2 text-xs text-ink-3">
                {p().actions.length} action
                {p().actions.length !== 1 ? "s were" : " was"} declined.
              </p>
            </Show>

            {/* Executing state */}
            <Show when={isRunning()}>
              <div class="mt-2 space-y-1">
                <For each={p().actions}>
                  {(action) => (
                    <div class="flex items-center gap-1.5 text-xs">
                      <ActionStatusIcon status={action.status} />
                      <span
                        class={
                          action.status === "executed"
                            ? "text-signal-green"
                            : action.status === "failed"
                              ? "text-signal-red"
                              : "text-ink-2"
                        }
                      >
                        {formatActionSummary(action)}
                      </span>
                    </div>
                  )}
                </For>
                <div class="flex items-center gap-1.5 text-xs text-signal-blue mt-1">
                  <Spinner size="sm" class="text-signal-blue" />
                  <span>Running actions...</span>
                </div>
              </div>
            </Show>

            {/* Executed / Partially executed state */}
            <Show
              when={
                p().status === "executed" || p().status === "partially_executed"
              }
            >
              <div class="mt-2 space-y-1">
                <For each={p().actions}>
                  {(action) => (
                    <div class="flex items-center gap-1.5 text-xs">
                      <ActionStatusIcon status={action.status} />
                      <span
                        class={
                          action.status === "executed"
                            ? "text-signal-green"
                            : action.status === "failed"
                              ? "text-signal-red"
                              : action.status === "rejected"
                                ? "text-ink-3"
                                : "text-ink-2"
                        }
                      >
                        {formatActionSummary(action)}
                      </span>
                    </div>
                  )}
                </For>
              </div>
            </Show>

            {/* Actionable states: collapsed or expanded */}
            <Show when={canAct()}>
              {/* Collapsed: impact summary chips */}
              <Show when={!expanded()}>
                <div class="mt-2">
                  <ProposalImpactSummary actions={p().actions} />
                </div>
              </Show>

              {/* Expanded action list */}
              <Show when={expanded()}>
                <div class="mt-2">
                  {/* Selection controls */}
                  <div class="flex items-center gap-1 text-xs mb-2">
                    <span class="text-ink-3">Select:</span>
                    <button
                      type="button"
                      class="text-ink-2 hover:text-ink font-medium"
                      onClick={selectAll}
                    >
                      All
                    </button>
                    <span class="text-ink-3">|</span>
                    <button
                      type="button"
                      class="text-ink-3 hover:text-ink"
                      onClick={deselectAll}
                    >
                      None
                    </button>
                  </div>

                  {/* Grouped action cards */}
                  <div class="space-y-2 max-h-[280px] overflow-y-auto">
                    <For each={groups()}>
                      {(group) => (
                        <ActionGroupSection
                          group={group}
                          selectedActionIds={selectedActions()}
                          onToggleGroup={() => toggleGroup(group.id)}
                          onToggleAction={toggleAction}
                          canAct={true}
                          compact={true}
                        />
                      )}
                    </For>
                  </div>
                </div>
              </Show>

              {/* Action buttons */}
              <div class="flex items-center justify-between gap-2 mt-3">
                <Dropdown
                  align="left"
                  trigger={
                    <Button variant="ghost" size="sm" disabled={isExecuting()}>
                      Reject
                    </Button>
                  }
                >
                  <div class="px-3 py-2 text-xs text-ink-2 border-b border-line">
                    Reject this proposal? The AI's suggestions will be
                    discarded.
                  </div>
                  <DropdownItem
                    class="text-signal-red hover:bg-signal-red-tint focus:bg-signal-red-tint"
                    onClick={handleReject}
                  >
                    Confirm Reject
                  </DropdownItem>
                </Dropdown>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleApprove}
                  disabled={selectedCount() === 0 || isExecuting()}
                  loading={isExecuting()}
                >
                  {selectedCount() === pendingActions().length
                    ? "Approve All"
                    : `Approve Selected (${selectedCount()})`}
                </Button>
              </div>
            </Show>
          </div>
        )}
      </Show>
    </div>
  );
};

const StatusIcon: Component<{ status: ProposalStatus }> = (props) => {
  const iconByStatus: Record<ProposalStatus, { path: string; class: string }> =
    {
      pending: {
        path: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
        class: "text-signal-magenta",
      },
      approved: {
        path: "M5 13l4 4L19 7",
        class: "text-signal-blue",
      },
      executing: {
        path: "M13 10V3L4 14h7v7l9-11h-7z",
        class: "text-signal-blue",
      },
      executed: {
        path: "M5 13l4 4L19 7",
        class: "text-signal-green",
      },
      partially_approved: {
        path: "M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
        class: "text-signal-amber",
      },
      partially_executed: {
        path: "M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
        class: "text-signal-amber",
      },
      rejected: {
        path: "M6 18L18 6M6 6l12 12",
        class: "text-signal-red",
      },
      expired: {
        path: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
        class: "text-ink-3",
      },
    };

  const icon = () => iconByStatus[props.status];

  return (
    <svg
      class={"w-3.5 h-3.5 shrink-0 " + icon().class}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d={icon().path}
      />
    </svg>
  );
};

const ActionStatusIcon: Component<{ status: ActionStatus }> = (props) => {
  return (
    <Show
      when={props.status === "executed"}
      fallback={
        <Show
          when={props.status === "failed"}
          fallback={
            <Show
              when={props.status === "approved" || props.status === "pending"}
              fallback={
                <svg
                  class="w-3 h-3 text-ink-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M20 12H4"
                  />
                </svg>
              }
            >
              <Spinner size="sm" class="w-3 h-3 text-signal-blue" />
            </Show>
          }
        >
          <svg
            class="w-3 h-3 text-signal-red shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Show>
      }
    >
      <svg
        class="w-3 h-3 text-signal-green shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 13l4 4L19 7"
        />
      </svg>
    </Show>
  );
};

const ChevronIcon: Component<{ expanded: boolean }> = (props) => (
  <svg
    class={
      "w-3.5 h-3.5 text-ink-3 transition-transform shrink-0 " +
      (props.expanded ? "rotate-180" : "")
    }
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const StatusLabel: Component<{ status: ProposalStatus }> = (props) => {
  const labels: Record<ProposalStatus, string> = {
    pending: "Pending",
    approved: "Approved",
    executing: "Executing...",
    executed: "Completed",
    partially_approved: "Partially Approved",
    partially_executed: "Partially Executed",
    rejected: "Rejected",
    expired: "Expired",
  };
  return <>{labels[props.status]}</>;
};

export default InlineProposal;
